import { createInteractionButtonEvent } from '@events/teacher/eventsCreator/interactionButton';
import {
  addEventRequest,
  amplitudeConfig,
  getCommonProperties,
  getLocalStorage,
  getUserProperties,
  isAiDebuggingEnabled,
  sendAmplitudeEvent,
} from '@geneo2-web/shared-ui';

import { IInteractionButtonEventArgs } from '@events/teacher/eventsCreator/inputArgs';
import {
  getLoginEvent,
  getLogoutEvent,
  login,
  logout,
} from '@events/teacher/eventsCreator/session';
import { Resource_ResourceEnum } from '@protos/content_management/content.db_pb';
import {
  ADD_RESOURCES,
  ANALYTICS_CHAPTER_SCORE,
  ANALYTICS_CLASS_SCORE,
  ANALYTICS_CLASS_SELECTION,
  ANALYTICS_STUDENT_SCORE,
  ASSESMENT_LIBRARY,
  ASSESSMENT_ASSIGN,
  ASSESSMENT_CONGRATULATIONS,
  ASSESSMENT_CREATE,
  ASSESSMENT_CURRENT,
  ASSESSMENT_QUESTIONBANK,
  ASSESSMENT_REVIEW,
  ASSESSMENT_VIEW,
  CHANGE_PASSWORD,
  CHAPTER_TOPIC_SELECTION,
  CHOOSE_PROFILE,
  CLICKER_ASSESSMENT,
  CLICKER_ASSESSMENT_MANAGE,
  CLICKER_ASSESSMENT_START,
  CONNECTED_CLASS_RESULTS,
  DIAGNOSTIC,
  DISCLAIMER,
  DOWNLOAD_SETTINGS,
  EDIT_LESSON_PLAN,
  ENTER_UID,
  FAQ,
  FORMATIVE,
  HELP,
  HOME,
  HOMEWORK,
  HOMEWORK_ASSIGN,
  HOMEWORK_CHAPTER_SELECTION,
  HOMEWORK_COMPLETED,
  HOMEWORK_CONGRATULATIONS,
  HOMEWORK_CREATE,
  HOMEWORK_CURRENT,
  HOMEWORK_CURRENT_CUSTOM,
  HOMEWORK_MANAGE,
  HOMEWORK_ONGOING,
  HOMEWORK_PERFORMANCE,
  HOMEWORK_QUESTIONBANK,
  HOMEWORK_REVIEW,
  HOMEWORK_SUBMISSION_CUSTOM,
  HOMEWORK_TOPIC_SELECTION,
  HOMEWORK_VIEW,
  LESSON_PLAN_LISTS,
  LOGIN_PASSWORD,
  MCB_APP_LOGIN,
  MCB_LOGIN,
  NOTIFICATION,
  OFFLINE_LESSON_PLAN_LISTS,
  OFFLINE_RESOURCE,
  OFFLINE_TEACHING_FLOW,
  OFFLINE_TEACH_CHAPTER_SELECTION,
  OFFLINE_TEACH_TOPIC_SELECTION,
  OTP_SCREEN,
  PROFILE,
  PROFILE_OTP_SCREEN,
  QPG_ASSESSMENT_CREATE,
  QPG_ASSESSMENT_QUESTION_BANK,
  QPG_ASSESSMENT_REVIEW,
  REGISTRATION,
  REPLACE_RESOURCE,
  REPORT,
  RESET_PASSWORD,
  RESET_PASSWORD_OTP,
  RESOURCE_PAGE,
  RESOURCE_UPLOAD,
  SEEALL_LESSON_PLAN_LIST,
  SELF_REGISTRATION,
  SELF_REGISTRATION_FORM,
  SET_PASSWORD,
  START_CLASSROOM,
  SWITCH_PROFILE,
  TEACHING_FLOW,
  TEACH_CHAPTER_SELECTION,
  TEACH_TOPIC_SELECTION,
  USER_NAME_REGISTRATION,
  VERIFY_DETAILS,
  VERIFY_EMAIL,
  VERIFY_PASSWORD,
  VERIFY_PHONE,
} from '../../routeHandling/RoutesNomenclature';
import { useAppSelector } from '../../reduxStore/reduxHooks';

//* --------------- Login Events ---------------
interface loginProps {
  teacherId: number;
  sessionId: string;
  isOffline: boolean;
}
export const aiLoginEvent = async (props: loginProps) => {
  const { teacherId, sessionId, isOffline } = props;

  try {
    login({
      teacherId: teacherId,
      sessionId: sessionId,
      loginTimestamp: new Date(),
    });

    const loginEventData = getLoginEvent({ isOffline: isOffline });
    if (loginEventData.timestamp) {
      await addEventRequest({
        key: loginEventData.timestamp?.toJsonString(),
        value: loginEventData.toJson(),
      });
    }
    //Amplitude
    const eventConfig = amplitudeConfig;
    const commonProperties = getCommonProperties(eventConfig);
    const userProps = {
      userID: teacherId ? teacherId : getLocalStorage('userId'),
      type: 'teacher',
      activeSession: getLocalStorage('activeSession'),
      deviceID: getLocalStorage('deviceID'),
      profileLoginDetailsId: getLocalStorage('profileLoginDetailsId'),
    };
    const userProperties = getUserProperties(userProps);
    if (eventConfig && userProps) {
      sendAmplitudeEvent(
        {
          type: 'login_success',
          props: {
            isOffline,
          },
        },
        eventConfig,
        userProps,
        userProperties,
        commonProperties
      );
    }
  } catch (error) {
    if (isAiDebuggingEnabled()) {
      throw error;
    } else {
      console.error(error);
    }
  }
};

//* --------------- Logout Events ---------------
export const aiLogoutEvent = async ({ isOffline }: { isOffline: boolean }) => {
  try {
    logout({ logoutTimestamp: new Date() });
    const logoutEvent = getLogoutEvent({ isOffline: isOffline });
    if (logoutEvent.timestamp) {
      await addEventRequest({
        key: logoutEvent.timestamp?.toJsonString(),
        value: logoutEvent.toJson(),
      });
    }
    //Amplitude
    const eventConfig = amplitudeConfig;
    const commonProperties = getCommonProperties(eventConfig);
    const userProps = {
      userID: getLocalStorage('userId'),
      type: 'teacher',
      activeSession: getLocalStorage('activeSession'),
      deviceID: getLocalStorage('deviceID'),
      profileLoginDetailsId: getLocalStorage('profileLoginDetailsId'),
    };
    const userProperties = getUserProperties(userProps);
    if (eventConfig && userProps) {
      sendAmplitudeEvent(
        {
          type: 'logout_success',
          props: {
            isOffline,
          },
        },
        eventConfig,
        userProps,
        userProperties,
        commonProperties
      );
    }
  } catch (error) {
    if (isAiDebuggingEnabled()) {
      throw error;
    } else {
      console.error(error);
    }
  }
};

//* --------------- Interaction Events ---------------

type IArgsWithUrl = IInteractionButtonEventArgs & {
  url: string;
  pathSegments?: string[];
  isOffline: boolean;
};

type IArgsWithoutUrl = Omit<IInteractionButtonEventArgs, 'url'> & {
  url?: undefined;
  pathSegments: string[];
  isOffline: boolean;
};
export const interactionEvent = async (
  props: IArgsWithUrl | IArgsWithoutUrl
) => {
  const { context, name, pathSegments, isOffline } = props;
  let { url } = props;

  if (url) {
    // if url is there getPathSegmentMapping will not be used
    url = isOffline ? `Offline_${url}` : url;
  } else if (pathSegments) {
    // Generate URL from pathSegments if no URL provided
    url = getPathSegmentMapping(pathSegments, isOffline) || '';
  }

  // final Validation in case Route Nomenclature is missing in pathMap
  if (!url) {
    console.error('No URL provided for interactionEvent');
    return;
  }

  try {
    const interactionEventData = createInteractionButtonEvent({
      url: url,
      context: context,
      name: name,
      lessonSessionId: props.lessonSessionId,
      resourceSessionId: props.resourceSessionId,
      isOffline: isOffline,
    });

    if (interactionEventData.timestamp) {
      await addEventRequest({
        key: interactionEventData.timestamp?.toJsonString(),
        value: interactionEventData.toJson(),
      });
    }

    //Amplitude
    const eventConfig = amplitudeConfig;
    const commonProperties = getCommonProperties(eventConfig);
    const userProps = {
      userID: getLocalStorage('userId'),
      type: 'teacher',
      activeSession: getLocalStorage('activeSession'),
      deviceID: getLocalStorage('deviceID'),
      profileLoginDetailsId: getLocalStorage('profileLoginDetailsId'),
    };
    const userProperties = getUserProperties(userProps);
    if (eventConfig && userProps) {
      sendAmplitudeEvent(
        {
          type: name,
          // type: context === 'Login_Navigation_bar' ? 'login_success' : name,
          props: {
            url,
            context,
            name,
            lessonSessionId: props.lessonSessionId,
            resourceSessionId: props.resourceSessionId,
            isOffline,
          },
        },
        eventConfig,
        userProps,
        userProperties,
        commonProperties
      );
    }
    // console.log('interactionEventData:', interactionEventData);
    // await TeacherEventsAPIServiceV1Client.teacherEventProcess(
    //   interactionEventData
    // );
  } catch (error) {
    if (isAiDebuggingEnabled()) {
      throw error;
    } else {
      console.error(error);
    }
  }
};

// ---------------------------------------------------------

// function getPathSegmentMapping(pathSegments: string[], isOffline: boolean) {
//   for (const segment of pathSegments) {
//     if (pathMap[segment]) {
//       if (isOffline) return `Offline_${pathMap[segment]}`;
//       else return pathMap[segment];
//     }
//   }
// }

function getPathSegmentMapping(pathSegments: string[], isOffline: boolean) {
  // Start with the longest possible match and work backwards
  for (let i = pathSegments.length; i > 1; i--) {
    const partialPath = pathSegments.slice(1, i).join('/'); // skip first '' from split
    console.log('partialPath:', partialPath);
    if (pathMap[partialPath]) {
      return isOffline
        ? `Offline_${pathMap[partialPath]}`
        : pathMap[partialPath];
    }
  }
  return undefined;
}

const removeOfflinePrefix = (path: string) => {
  return path.replace('/offline/', '');
};

const pathMap = {
  // AUTH
  [ENTER_UID.slice(1)]: 'Teacher_login',
  [OTP_SCREEN.slice(1)]: 'Teacher_otp_screen',
  [VERIFY_DETAILS.slice(1)]: 'Teacher_self_registration_verify_details',
  [SELF_REGISTRATION.slice(1)]: 'Teacher_self_registration',
  [USER_NAME_REGISTRATION.slice(1)]: 'Teacher_registration_username',
  [SELF_REGISTRATION_FORM.slice(1)]: 'Teacher_self_registration_form',
  [VERIFY_PHONE.slice(1)]: 'Teacher_verify_phone',
  [VERIFY_EMAIL.slice(1)]: 'Teacher_verify_email',
  [SET_PASSWORD.slice(1)]: 'Teacher_set_password',
  [RESET_PASSWORD_OTP.slice(1)]: 'Teacher_reset_password_otp',
  [CHOOSE_PROFILE.slice(1)]: 'Teacher_choose_profile',
  [SWITCH_PROFILE.slice(1)]: 'Teacher_switch_profile',
  [REGISTRATION.slice(1)]: 'Teacher_registration',
  [LOGIN_PASSWORD.slice(1)]: 'Teacher_login_password',

  // MCB Login
  [MCB_LOGIN.slice(1)]: 'Teacher_mcb_login',
  [MCB_APP_LOGIN.slice(1)]: 'Teacher_mcb_app_login',

  //Home
  [HOME.slice(1)]: 'Teacher_Home',
  [SEEALL_LESSON_PLAN_LIST.slice(1)]: 'Teacher_See_All_Lesson_Plan',

  // Homework
  [HOMEWORK_CHAPTER_SELECTION.slice(1)]: 'Teacher_homework_chapter_selection',
  [HOMEWORK_TOPIC_SELECTION.slice(1)]: 'Teacher_homework_topic_selection',
  [HOMEWORK_MANAGE.slice(1)]: 'Teacher_homework_manage',
  [HOMEWORK_ONGOING.slice(1)]: 'Teacher_ongoing_homework',
  [HOMEWORK_COMPLETED.slice(1)]: 'Teacher_homework_completed',
  [HOMEWORK_REVIEW.slice(1)]: 'Teacher_homework_review',
  [HOMEWORK_QUESTIONBANK.slice(1)]: 'Teacher_homework_questionBank',
  [HOMEWORK_PERFORMANCE.slice(1)]: 'Teacher_homework_performance',
  [HOMEWORK_CURRENT_CUSTOM.slice(1)]: 'Teacher_current_custom_homework',
  [HOMEWORK_SUBMISSION_CUSTOM.slice(1)]: 'Teacher_submission_custom_homework',
  [HOMEWORK_CREATE.slice(1)]: 'Teacher_homework_create',
  [HOMEWORK_ASSIGN.slice(1)]: 'Teacher_homework_assign',
  [HOMEWORK_CONGRATULATIONS.slice(1)]: 'Teacher_homework_congratulations',
  [HOMEWORK_VIEW.slice(1)]: 'Teacher_homework_view',
  [HOMEWORK_CURRENT.slice(1)]: 'Teacher_current',

  // Assessment
  [FORMATIVE.slice(1)]: 'Teacher_assessment_formative',
  [CHAPTER_TOPIC_SELECTION.slice(1)]:
    'Teacher_assessment_chapter_topic_selection',
  [HOMEWORK.slice(1)]: 'Teacher_assessment_homework',
  [DIAGNOSTIC.slice(1)]: 'Teacher_assessment_diagnostic',
  [ASSESSMENT_CREATE.slice(1)]: 'Teacher_assessment_create',
  [ASSESSMENT_ASSIGN.slice(1)]: 'Teacher_assessment_assign',
  [ASSESMENT_LIBRARY.slice(1)]: 'Teacher_assessment_library',
  [ASSESSMENT_CONGRATULATIONS.slice(1)]: 'Teacher_assessment_congratulations',
  [ASSESSMENT_VIEW.slice(1)]: 'Teacher_assessment_view',
  [ASSESSMENT_CURRENT.slice(1)]: 'Teacher_assessment_current',

  // Clicker Assessment
  [CLICKER_ASSESSMENT_MANAGE.slice(1)]: 'Teacher_clicker-assessment-manage',
  [CLICKER_ASSESSMENT_START.slice(1)]: 'Teacher_clicker-assessment-start',
  [CLICKER_ASSESSMENT.slice(1)]: 'Teacher_clicker-assessment',
  [ASSESSMENT_REVIEW.slice(1)]: 'Teacher_assessment-review',
  [ASSESSMENT_QUESTIONBANK.slice(1)]: 'Teacher_assessment-question-bank',
  [QPG_ASSESSMENT_CREATE.slice(1)]: 'Teacher_QPG_assessment_create',
  [QPG_ASSESSMENT_REVIEW.slice(1)]: 'Teacher_QPG_assessment_review',
  [QPG_ASSESSMENT_QUESTION_BANK.slice(1)]:
    'Teacher_QPG_assessment_question-bank',

  //Analytics
  [ANALYTICS_CLASS_SELECTION.slice(1)]: 'Teacher_analytics_dashboard',
  [ANALYTICS_CLASS_SCORE.slice(1)]: 'Teacher_analytics_classScore',
  [ANALYTICS_CHAPTER_SCORE.slice(1)]: 'Teacher_analytics_chapterScore',
  [ANALYTICS_STUDENT_SCORE.slice(1)]: 'Teacher_analytics_studentScore',

  // Teach
  [TEACH_CHAPTER_SELECTION.slice(1)]: 'Teacher_teach_chapter_selection',
  [removeOfflinePrefix(OFFLINE_TEACH_CHAPTER_SELECTION)]:
    'Teacher_teach_chapter_selection',

  [TEACH_TOPIC_SELECTION.slice(1)]: 'Teacher_teach_topic_selection',
  [removeOfflinePrefix(OFFLINE_TEACH_TOPIC_SELECTION)]:
    'Teacher_teach_topic_selection',

  [LESSON_PLAN_LISTS.slice(1)]: 'Teacher_lesson_plan_lists',
  [removeOfflinePrefix(OFFLINE_LESSON_PLAN_LISTS)]: 'Teacher_lesson_plan_lists',

  [TEACHING_FLOW.slice(1)]: 'Teacher_teaching_flow',
  [removeOfflinePrefix(OFFLINE_TEACHING_FLOW)]: 'Teacher_teaching_flow',

  [EDIT_LESSON_PLAN.slice(1)]: 'Teacher_edit_lesson',

  [ADD_RESOURCES.slice(1)]: 'Teacher_add_resources',

  [REPLACE_RESOURCE.slice(1)]: 'Teacher_replace_resource',

  [RESOURCE_PAGE.slice(1)]: 'Teacher_resource_page',
  [removeOfflinePrefix(OFFLINE_RESOURCE)]: 'Teacher_resource_page',

  [RESOURCE_UPLOAD.slice(1)]: 'Teacher_resource_upload',

  // Sidebar
  [PROFILE.slice(1)]: 'Teacher_profile',
  [PROFILE_OTP_SCREEN.slice(1)]: 'Teacher_profile_otp_screen',
  [CHANGE_PASSWORD.slice(1)]: 'Teacher_download_settings',
  [NOTIFICATION.slice(1)]: 'Teacher_notification',
  [HELP.slice(1)]: 'Teacher_help',
  [FAQ.slice(1)]: 'Teacher_faq',
  [DISCLAIMER.slice(1)]: 'Teacher_disclaimer',
  [VERIFY_PASSWORD.slice(1)]: 'Teacher_verify_password',
  [RESET_PASSWORD.slice(1)]: 'Teacher_reset_password',
  [DOWNLOAD_SETTINGS.slice(1)]: 'Teacher_download_settings',
  [REPORT.slice(1)]: 'Teacher_report',

  //ConnectedClassroom
  [START_CLASSROOM.slice(1)]: 'Teacher_connected_classroom',
  [CONNECTED_CLASS_RESULTS.slice(1)]: 'Teacher_connected_class_results',
};

// ---------------------------------------------------------

export const getResourceType = (number: number): string => {
  return Resource_ResourceEnum[number] || Resource_ResourceEnum[0];
};

// ---------------------------------------------------------

/*

  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  await interactionEvent({
    context: 'context',
    name: 'name',
    pathSegments: pathSegments,
    isOffline: isOffline,
  });

   await interactionEvent({
    url:"",
    context: 'context',
    name: 'name',
    pathSegments: pathSegments,
    isOffline: isOffline,
  });

  .toUpperCase()
  .replace(/\s+/g, '_')
  .replace(/[:.]/g, '')
  .replace(/\//g, '_/_')

 */
